import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { withSitecoreContext, Text, RichText } from '@sitecore-jss/sitecore-jss-react';
import { DealerLocatorModel } from './models/dealerLocatorDetail.model';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import SocialMediaBar from '../@shared/socialMediaBar';
import Picture from '../@shared/picture';
import DealerLocatorContacts from './components/dealerContactsSection';
import { TDealerLocatorDetailProps } from './types/dealerLocatorDetail.types';
import BusinessHoursAndCertificationsSection from './components/businessHoursAndCertificationsSection';
import { replaceSubStrings } from '../../utils/string.utils';
import DealerEquipmentsServices from './components/dealerEquipmentsServices';
import './styles.scss';

const DealerLocatorDetail: FC<TDealerLocatorDetailProps> = (props) => {
  const { t } = useTranslation();
  const model = new DealerLocatorModel(props);

  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <div className="dealer-locator-detail">
      <div className="dealer-locator-detail__header">
        <Text
          field={{ value: t('cnhi-dealerLocatorDetailEyebrow') }}
          tag="div"
          className={'dealer-locator-detail__eyebrow'}
          editable={false}
        />
        <Text field={model.dealerName} tag={model.headingTag} className={'dealer-locator-detail__title'} editable={false} />
        <Text
          field={{ value: replaceSubStrings(t('cnhi-dealerLocatorDetailSubtitle'), '{dealerName}', model?.dealerName?.value) }}
          tag="div"
          className="dealer-locator-detail__subtitle"
          editable={false}
        />
        {model.socialMediaBarProps && (
          <SocialMediaBar {...model.socialMediaBarProps} extraClassName="dealer-locator-detail__social-media-bar" />
        )}
      </div>
      {model.mediaExists && (
        <div className={'dealer-locator-detail__media'}>
          {model.logoImage && (
            <Picture
              productImageProps={{
                desktopRendition: { url: model?.logoImage },
                imageClassName: 'dealer-locator-detail__media__logo-image',
                pictureClassName: 'dealer-locator-detail__media__logo-image-wrapper',
              }}
            ></Picture>
          )}
          {model.dealerImage && (
            <div className={'dealer-locator-detail__media__dealer-image-wrapper'}>
              <Picture
                productImageProps={{
                  desktopRendition: { url: model.dealerImage },
                  pictureClassName: 'dealer-locator-detail__media__dealer-image',
                }}
              ></Picture>
            </div>
          )}
        </div>
      )}
      {model.descriptionExists && (
        <RichText tag="div" field={model.description} className={'dealer-locator-detail__description rich-text'} />
      )}
      <DealerLocatorContacts
        apiKey={model.dealerLocatorGoogleMapsAPIKey}
        addressDetails={model.addressDetails}
        latitude={model.latitude}
        longitude={model.longitude}
      />
      {model.businessHoursAndCertificationsSectionExists && (
        <BusinessHoursAndCertificationsSection
          businessHours={model?.businessHours}
          hasBusinessHours={model?.hasBusinessHours}
          certifications={model?.certifications}
          hasCertifications={model?.hasCertifications}
        />
      )}
      {model?.promotionsExist && (
        <section className={'dealer-locator-detail__promotions'}>
          <Text
            field={{ value: t('cnhi-dealerLocatorPromotions') }}
            tag="div"
            className={'dealer-locator-detail__promotions__title'}
            editable={false}
          />
          <RichText field={model?.promotions} className={'dealer-locator-detail__promotions__text rich-text'} editable={false} />
        </section>
      )}
      <DealerEquipmentsServices filterDetails={model.equipments} titeDictonaryKey="cnhi-dealerLocatorEquipmentsTitle" />
      <DealerEquipmentsServices filterDetails={model.services} titeDictonaryKey="cnhi-dealerLocatorServicesTitle" />
    </div>
  );
};

export default withSitecoreContext()(DealerLocatorDetail);
